<template>
    <div v-bind:class="{ 'auto-cursor': toolTip.display }">
        <pc-loader v-bind:loading="element.Loading">
            <div
                class="dropdown context-menu"
                data-test-role="context-menu">
                <img
                    src="~Images/icons/dashboard/menu.svg"
                    class="graph-menu dropdown-toggle m-1"
                    data-bs-toggle="dropdown" />
                <div class="dropdown-menu dropdown-menu-right">
                    <button
                        v-if="editMode"
                        class="dropdown-item"
                        type="button"
                        v-on:click="editWidget">
                        {{ editWidgetOption }}
                    </button>
                    <button
                        class="dropdown-item"
                        data-test-role="export-png"
                        type="button"
                        v-on:click="exportGraph('png')">
                        {{ exportPngBtnLabel }}
                    </button>
                    <button
                        class="dropdown-item"
                        data-test-role="export-svg"
                        type="button"
                        v-on:click="exportGraph('svg')">
                        {{ exportSvgBtnLabel }}
                    </button>
                    <button
                        v-if="editMode"
                        class="dropdown-item"
                        type="button"
                        v-on:click="removeWidget">
                        {{ deleteBtnLabel }}
                    </button>
                </div>
            </div>
            <div
                v-if="toolTip.display"
                ref="tooltip"
                v-bind:style="toolTipStyle"
                class="card p-2 rounded pc-tooltip-element">
                <table>
                    <tr
                        v-for="(value, key) in toolTip.content"
                        v-bind:key="key">
                        <td class="fw-light text-end">
                            {{ key }}
                        </td>
                        <td>:</td>
                        <td class="fw-bold text-start">
                            {{ value }}
                        </td>
                        <!-- Loop through tooltip content and display as list-->
                    </tr>
                </table>
            </div>
            <div
                ref="graph"
                v-bind:style="{ height: element.dimensions.height + 'px', width: element.dimensions.width + 'px' }" />
        </pc-loader>
    </div>
</template>

<script>
    import * as d3 from "d3-selection";
    import * as vega from "vega";
    import { GRAPH_TYPE, calculatePosition, AXIS_PROPERTY_TYPE } from "./dashboard-constants.js";
    import { BUILT_IN_FIELD_TYPES } from "./builtInFieldStore.js";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore.js";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/graph.i18n";

    const hiddenFilter = "rgba(0,0,0,0)";
    const brightenFilter = "rgba(255,255,255,0.3)";

    export const VegaSpecificationSchema = "https://vega.github.io/schema/vega/v3.json";

    export function HoverSignal(markType) {
        return {
            name: "hoverEvent",
            value: null,
            on: [
                { events: markType + ":mouseover", update: "datum" },
                { events: markType + ":mouseout", update: "null" }
            ]
        };
    }

    export function StackedGraphLegend(stackAxisLabel, stackedLegendColumns) {
        return {
            type: "symbol",
            fill: "color",
            direction: "horizontal",
            columns: stackedLegendColumns,
            orient: "bottom",
            offset: 5,
            strokeColor: "#000",
            title: stackAxisLabel,
            padding: 7
        };
    }

    export const LabelOptionDefaults = {
        angle: { value: 310 },
        align: { value: "right" },
        baseline: { value: "middle" },
        ellipsis: { value: ".." },
        limit: { value: 110 }
    };

    export default {
        props: {
            element: { type: Object, required: true },
            editMode: { type: Boolean, required: true },
            graphBag: { type: Object, required: true },
            stackedLegendColumns: { type: Number, required: false, default: 0 },
            // eslint-disable-next-line vue/require-default-prop
            graphData: { type: [Array, String], required: false }
        },
        emits: ["edit", "remove", "getLatestData", "focus"],
        data: function () {
            return {
                graphTypeConstant: GRAPH_TYPE,
                timeout: null,
                toolTip: { x: 0, y: 0, display: false, content: {}, offSet: { x: 10, y: 10 } },
                builtInMargin: 5,
                view: null
            };
        },
        computed: {
            dimensions() {
                return {
                    height: this.element.dimensions.height - this.builtInMargin,
                    width: this.element.dimensions.width - this.builtInMargin
                };
            },
            toolTipStyle() {
                return {
                    transform: "translate(" + this.toolTip.x + "px, " + this.toolTip.y + "px)",
                    position: "absolute"
                };
            },
            canGraphUseLegend() {
                let hasLegend = false;

                if (this.graphBag.Graph.TypeId == GRAPH_TYPE.stacked) {
                    const stackedAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Stacked");
                    hasLegend = this.canUseLegend(stackedAxis);
                }
                if (this.graphBag.Graph.TypeId == GRAPH_TYPE.pie) {
                    const columnAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Column");
                    hasLegend = this.canUseLegend(columnAxis);
                }
                return hasLegend;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            graphData: {
                async handler(newValue) {
                    if (newValue) {
                        await this.renderGraph(newValue);
                    }
                },
                deep: true
            },
            async stackedLegendColumns() {
                if (this.graphData) {
                    await this.renderGraph(this.graphData);
                }
            },
            dimensions: function (newValue) {
                if (this.view) {
                    this.view.signal("height", newValue.height).run("enter");
                    this.view.signal("width", newValue.width).run("enter");
                    this.adjustLegend();
                }
            }
        },
        async mounted() {
            if (this.graphData) {
                // If there is data provided render the graph
                this.renderGraph(this.graphData);
            } else {
                // No data provided. Request latest data from source.
                this.$nextTick(this.getLatestData());
            }
        },
        methods: {
            editWidget: function () {
                this.$emit("edit");
            },
            removeWidget: function () {
                this.$emit("remove");
            },
            exportGraph: function (exportType) {
                const controller = this;
                this.view.toImageURL(exportType).then(function (url) {
                    const fileName = controller.graphBag.Graph.Title + "." + exportType;

                    // Check for IE or Edge for special browser handling
                    if (window.navigator && window.navigator.msSaveBlob) {
                        if (exportType === "png") {
                            const blob = controller.dataURItoBlob(url);
                            window.navigator.msSaveBlob(blob, fileName);
                        } else {
                            // Get the Blob object from the object url
                            const xhr = new XMLHttpRequest();
                            xhr.open("GET", url, true);
                            xhr.responseType = "blob";
                            xhr.onload = function () {
                                if (this.status == 200) {
                                    const myBlob = this.response;
                                    window.navigator.msSaveBlob(myBlob, fileName);
                                }
                            };
                            xhr.send();
                        }
                    } else {
                        // Use the more generic DownloadFile in Util.js once added
                        const link = document.createElement("a");
                        link.setAttribute("href", url);
                        link.setAttribute("target", "_blank");
                        link.setAttribute("download", fileName);
                        link.dispatchEvent(new MouseEvent("click"));
                    }
                });
            },
            dataURItoBlob: function (dataURI) {
                const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
                const binary = atob(dataURI.split(",")[1]);
                const array = [];
                for (let i = 0; i < binary.length; i++) {
                    array.push(binary.charCodeAt(i));
                }
                return new Blob([new Uint8Array(array)], { type: mime });
            },
            getAxisName: function (axis) {
                const showAggregator = this.getAxisPropertyValue(axis, AXIS_PROPERTY_TYPE.AxisType) == "Row";
                let axisName = "";
                if (axis.DataFieldId) {
                    axisName = this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                        axis.DataFieldId
                    ).Name;
                    if (showAggregator) {
                        const aggregator = this.getAxisPropertyValue(axis, AXIS_PROPERTY_TYPE.Aggregate);
                        axisName = axisName + " " + this.formatPropertyName(aggregator);
                    }
                } else if (axis.BuiltInFieldId) {
                    axisName = this.$store.getters[BUILT_IN_FIELD_TYPES.getters.getBuiltInFieldNameById](
                        axis.BuiltInFieldId
                    );
                } else if (axis.AxisName) {
                    axisName = axis.AxisName;
                } else {
                    throw { message: "Error getting axis name", context: this, args: axis };
                }
                return axisName.replace("-", " ");
            },
            getGraphConstructor: function (data) {
                const dataSpec = this.getDataSpec(data);

                // If the GraphSpec is already provided in the graphBag (as may be the case if the graph
                //  component is being used outside the Dashboards page), add in the data and return
                if (this.graphBag.GraphSpec) {
                    const graphSpec = this.graphBag.GraphSpec;
                    graphSpec["data"] = dataSpec;
                    return graphSpec;
                }

                const xAxisColumn = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Column");
                const yAxisColumn = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Row");

                const graphType = this.graphBag.Graph.TypeId;
                switch (graphType) {
                    case this.graphTypeConstant.bar:
                        return this.getBarSpecification(dataSpec, xAxisColumn, yAxisColumn);
                    case this.graphTypeConstant.line:
                        return this.getLineSpecification(dataSpec, xAxisColumn, yAxisColumn);
                    case this.graphTypeConstant.pie:
                        return this.getPieSpecification(dataSpec);
                    case this.graphTypeConstant.stacked:
                        return this.getStackedSpecification(dataSpec, xAxisColumn, yAxisColumn);
                    default:
                        // raise something went wrong with an alert that we can find the issue fast
                        throw { message: this.badGraphMsg, context: this, args: this.graphBag };
                }
            },
            getAxisByProperty: function (property, propertyValue) {
                const axes = this.graphBag.Axes;
                for (let i = 0; i < axes.length; i++) {
                    const axis = axes[i];
                    const propValue = this.getAxisPropertyValue(axis, property);
                    if (propValue == propertyValue) {
                        return axis;
                    }
                }
                return null;
            },
            getPropertyOrDefault: function (o, property, defaultValue) {
                let value = o[property];
                if (value == undefined || value == null) {
                    value = defaultValue;
                }
                return value;
            },
            getAxisPropertyValue: function (axis, axisProperty) {
                return axis.AxisProperties[axisProperty];
            },
            formatPropertyName: function (property) {
                return property.charAt(0).toUpperCase() + property.slice(1);
            },
            getDataSpec: function (data) {
                const graphBag = this.graphBag;

                // If the Graph.source is a DataObject (as may be the case if the graph component is being
                //  used outside the Dashboards page), define the dataSpec from the input data and return
                if (graphBag.Graph.source == "DataObject") {
                    const dataSpec = {
                        name: "table",
                        values: data
                    };
                    // If a Graph.transform is provided, include it in the dataSpec
                    if (graphBag.Graph.transform) {
                        dataSpec.transform = graphBag.Graph.transform;
                    }
                    return dataSpec;
                }

                const xAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Column");
                const yAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Row");

                const xAxisName = this.getAxisName(xAxis);
                const yAxisName = this.getAxisName(yAxis);

                // Build parse object to specify output types instead of relying on the vega parser to auto detect.
                const parser = {};
                for (let i = 0; i < graphBag.Axes.length; i++) {
                    const axis = graphBag.Axes[i];
                    const type = this.getAxisPropertyValue(axis, AXIS_PROPERTY_TYPE.AxisType);
                    const axisName = this.getAxisName(axis);
                    if (type == "Row") {
                        parser[axisName] = "number";
                    } else {
                        parser[axisName] = "string";
                    }
                }

                // NOTE: Vega CSV parser uses Datalib.js which is a utitliy library specifically created for Vega.
                // This isn't documented clearly which hides the properties that can be used to parse CSV files properly
                const dataSpec = {
                    name: "table",
                    values: data,
                    format: { type: "csv", parse: parser, delimiter: "," },
                    transform: [
                        {
                            type: "aggregate",
                            groupby: [xAxisName],
                            ops: ["average"],
                            fields: [yAxisName],
                            as: ["sum_yaxis"]
                        }
                    ]
                };

                if (graphBag.Graph.TypeId == this.graphTypeConstant.stacked) {
                    const stackedAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Stacked");
                    const stackedAxisName = this.getAxisName(stackedAxis);
                    dataSpec.transform[0] = {
                        type: "stack",
                        groupby: [xAxisName],
                        sort: { field: stackedAxisName },
                        field: yAxisName
                    };
                }

                return dataSpec;
            },
            getStackedSpecification: function (data, xAxis) {
                const xAxisName = this.getAxisName(xAxis);
                const xAxisLabel = this.getAxisPropertyValue(xAxis, AXIS_PROPERTY_TYPE.Label);
                const yAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Row");
                const yAxisLabel = this.getAxisPropertyValue(yAxis, AXIS_PROPERTY_TYPE.Label);
                //var yAxisName = this.getAxisName(yAxis);
                const stackedAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Stacked");
                const stackAxisLabel = this.getAxisPropertyValue(stackedAxis, AXIS_PROPERTY_TYPE.Label);
                const stackedAxisName = this.getAxisName(stackedAxis);
                const colorScheme = this.getPropertyOrDefault(
                    xAxis.AxisProperties,
                    AXIS_PROPERTY_TYPE.Color,
                    "category10"
                );

                const spec = {
                    $schema: VegaSpecificationSchema,
                    autosize: "fit",
                    signals: [HoverSignal("rect")],
                    scales: [
                        {
                            name: "x",
                            type: "band",
                            range: "width",
                            padding: 0.25,
                            domain: { data: "table", field: xAxisName }
                        },
                        {
                            name: "y",
                            type: "linear",
                            range: "height",
                            nice: true,
                            zero: true,
                            domain: { data: "table", field: "y1" }
                        },
                        {
                            name: "color",
                            type: "ordinal",
                            range: { scheme: colorScheme },
                            domain: { data: "table", field: stackedAxisName },
                            zero: false,
                            reverse: false
                        }
                    ],
                    axes: [
                        {
                            orient: "bottom",
                            scale: "x",
                            zindex: 1,
                            encode: {
                                labels: {
                                    update: this.LabelOptionDefaults
                                }
                            },
                            title: xAxisLabel,
                            titleFontSize: 14,
                            titlePadding: 4
                        },
                        {
                            grid: true,
                            orient: "left",
                            scale: "y",
                            title: yAxisLabel,
                            titleFontSize: 14,
                            titlePadding: 4,
                            zindex: 0
                        }
                    ],
                    marks: [
                        {
                            type: "rect",
                            from: { data: "table" },
                            encode: {
                                enter: {
                                    x: { scale: "x", field: xAxisName },
                                    width: { scale: "x", band: 1, offset: -10 },
                                    y: { scale: "y", field: "y0" },
                                    y2: { scale: "y", field: "y1" }
                                },
                                update: {
                                    fill: { scale: "color", field: stackedAxisName },
                                    fillOpacity: [
                                        {
                                            test: "hoverEvent == datum",
                                            value: 0.9
                                        },
                                        { value: 1 }
                                    ]
                                }
                            }
                        }
                    ],
                    title: {
                        text: this.graphBag.Graph.Title,
                        fontSize: 15
                    }
                };
                if (this.canUseLegend(stackedAxis)) {
                    spec["legends"] = StackedGraphLegend(stackAxisLabel, this.stackedLegendColumns);
                }

                // Add data to Stacked Graph Specification
                spec["data"] = data;

                return spec;
            },
            getBarSpecification: function (data, xAxis, yAxis) {
                const xAxisName = this.getAxisName(xAxis);
                const xAxisLabel = this.getAxisPropertyValue(xAxis, AXIS_PROPERTY_TYPE.Label);
                const yAxisLabel = this.getAxisPropertyValue(yAxis, AXIS_PROPERTY_TYPE.Label);
                const colorScheme = this.getPropertyOrDefault(xAxis.AxisProperties, AXIS_PROPERTY_TYPE.Color, "blue");
                const style = this.$app.getExportedCssVars();

                const spec = {
                    $schema: VegaSpecificationSchema,
                    autosize: "fit",
                    scales: [
                        {
                            name: "xscale",
                            type: "band",
                            domain: { data: "table", field: xAxisName },
                            range: "width",
                            padding: 0.4,
                            round: true
                        },
                        {
                            name: "yscale",
                            domain: { data: "table", field: "sum_yaxis" },
                            nice: true,
                            range: "height"
                        }
                    ],
                    signals: [HoverSignal("rect")],
                    axes: [
                        {
                            orient: "bottom",
                            scale: "xscale",
                            labelOverlap: true,
                            encode: {
                                labels: {
                                    update: this.LabelOptionDefaults
                                }
                            },
                            title: xAxisLabel,
                            titleFontSize: 13,
                            titlePadding: 5,
                            zindex: 1
                        },
                        {
                            grid: true,
                            title: yAxisLabel,
                            titleFontSize: 13,
                            titlePadding: 5,
                            orient: "left",
                            scale: "yscale"
                        }
                    ],
                    marks: [
                        {
                            type: "rect",
                            from: { data: "table" },
                            encode: {
                                enter: {
                                    tooltip: {
                                        signal: `{'${xAxisLabel}': datum['${xAxisName}'], '${yAxisLabel}': datum['sum_yaxis']}`
                                    },
                                    x: { scale: "xscale", field: xAxisName },
                                    width: { scale: "xscale", band: 1 },
                                    y: { scale: "yscale", field: "sum_yaxis" },
                                    y2: { scale: "yscale", value: 0 }
                                },
                                update: {
                                    fill: { value: style[colorScheme] },
                                    fillOpacity: [
                                        {
                                            test: "hoverEvent == datum",
                                            value: 0.9
                                        },
                                        { value: 1 }
                                    ]
                                }
                            }
                        }
                    ],
                    title: {
                        text: this.graphBag.Graph.Title,
                        fontSize: 15
                    }
                };
                // Add data to Bar Graph Specification
                spec["data"] = data;

                return spec;
            },
            getLineSpecification: function (data, xAxis, yAxis) {
                const xAxisName = this.getAxisName(xAxis);
                const xAxisLabel = this.getAxisPropertyValue(xAxis, AXIS_PROPERTY_TYPE.Label);
                const yAxisName = this.getAxisName(yAxis);
                const yAxisLabel = this.getAxisPropertyValue(yAxis, AXIS_PROPERTY_TYPE.Label);
                const colorScheme = this.getPropertyOrDefault(
                    xAxis.AxisProperties,
                    AXIS_PROPERTY_TYPE.Color,
                    "category10"
                );
                const categoryAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Category");
                const categoryName = this.getAxisName(categoryAxis);
                const categoryLabel = this.getAxisPropertyValue(categoryAxis, AXIS_PROPERTY_TYPE.Label);

                const spec = {
                    $schema: VegaSpecificationSchema,
                    autosize: "fit",
                    scales: [
                        {
                            name: "xscale",
                            type: "time",
                            domain: { data: "table", field: xAxisName },
                            range: "width",
                            nice: true
                        },
                        {
                            name: "yscale",
                            type: "linear",
                            domain: { data: "table", field: yAxisName },
                            nice: true,
                            zero: true,
                            range: "height"
                        },
                        {
                            name: "color",
                            type: "ordinal",
                            range: { scheme: colorScheme },
                            domain: { data: "table", field: categoryName }
                        }
                    ],
                    axes: [
                        {
                            orient: "bottom",
                            scale: "xscale",
                            title: xAxisLabel,
                            titleFontSize: 14,
                            titlePadding: 4
                        },
                        {
                            orient: "left",
                            scale: "yscale",
                            title: yAxisLabel,
                            titleFontSize: 14,
                            titlePadding: 4
                        }
                    ],
                    marks: [
                        {
                            type: "group",
                            from: {
                                facet: {
                                    name: "series",
                                    data: "table",
                                    groupby: categoryName
                                }
                            },
                            marks: [
                                {
                                    type: "line",
                                    from: { data: "series" },
                                    encode: {
                                        enter: {
                                            x: { scale: "xscale", field: xAxisName },
                                            y: { scale: "yscale", field: yAxisName },
                                            stroke: { scale: "color", field: categoryName },
                                            strokeWidth: { value: 2 }
                                        },
                                        update: {
                                            strokeOpacity: { value: 1 }
                                        },
                                        hover: {
                                            strokeOpacity: { value: 0.5 }
                                        }
                                    }
                                },
                                {
                                    type: "line",
                                    from: { data: "series" },
                                    encode: {
                                        enter: {
                                            x: { scale: "xscale", field: xAxisName },
                                            y: { scale: "yscale", field: yAxisName },
                                            stroke: { scale: "color", field: categoryName },
                                            strokeWidth: { value: 2 }
                                        },
                                        update: {
                                            fill: { value: hiddenFilter },
                                            strokeOpacity: { value: 1 }
                                        },
                                        hover: {
                                            fill: { value: brightenFilter },
                                            strokeOpacity: { value: 0.5 }
                                        }
                                    }
                                },
                                {
                                    type: "symbol",
                                    from: { data: "series" },
                                    encode: {
                                        enter: {
                                            tooltip: {
                                                // eslint-disable-next-line max-len
                                                signal: `{'${categoryLabel}': datum['${categoryName}'], '${xAxisLabel}': datum['${xAxisName}'], '${yAxisLabel}': datum['${yAxisName}'] }`
                                            },
                                            x: { scale: "xscale", field: xAxisName },
                                            y: { scale: "yscale", field: yAxisName },
                                            size: { value: 150 },
                                            fill: { value: hiddenFilter }
                                        }
                                    }
                                }
                            ]
                        }
                    ],
                    title: {
                        text: this.graphBag.Graph.Title,
                        fontSize: 15
                    }
                };
                spec["data"] = data;
                spec["legends"] = {
                    type: "symbol",
                    title: categoryLabel,
                    fill: "color",
                    direction: "horizontal",
                    orient: "bottom",
                    offset: 0,
                    columns: 4,
                    symbolStrokeColor: "#fff",
                    symbolStrokeWidth: 1,
                    columnPadding: 0
                };
                return spec;
            },
            canUseLegend: function (/*axis*/) {
                let useLegend = false;
                // TODO: Was requested to just display the axis irregardless of how many categories exists for a graph
                // Keep this as a placeholder in case we want to come back to the idea of filtering legends.
                // if(axis.DataFieldId){
                //     var dataField = this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](axis.DataFieldId);
                //     if (dataField.ListId) {
                //         var listItems = this.$store.getters[PROGRAM_TYPES.getters.getAllListItemsByDatafieldId](
                //             axis.DataFieldId
                //         );
                //         if (listItems.length <= 12) {
                //             useLegend = true;
                //         }
                //     }
                // } else if(axis.BuiltInFieldId){
                //     var results = this.$store.getters[DASHBOARD_TYPES.getters.getGraphDataById](axis.GraphId);
                //     var numItemsPossible = results.split(/\r\n|\r|\n/).length;
                //     if (numItemsPossible <= 12) {
                //         useLegend = true;
                //     }
                // }
                useLegend = true;
                return useLegend;
            },
            getPieSpecification: function (data) {
                const xAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Column");
                //var yAxis = this.getAxisByProperty(AXIS_PROPERTY_TYPE.AxisType, "Row");
                const xAxisName = this.getAxisName(xAxis);
                const xAxisLabel = this.getAxisPropertyValue(xAxis, AXIS_PROPERTY_TYPE.Label);
                //var yAxisLabel = this.getAxisName(yAxis);
                const colorScheme = this.getPropertyOrDefault(
                    xAxis.AxisProperties,
                    AXIS_PROPERTY_TYPE.Color,
                    "category10"
                );

                const spec = {
                    $schema: VegaSpecificationSchema,
                    autosize: "fit",
                    scales: [
                        {
                            name: "color",
                            type: "ordinal",
                            range: { scheme: colorScheme },
                            domain: { data: "table", field: xAxisName },
                            zero: false,
                            reverse: false
                        }
                    ],
                    signals: [
                        {
                            name: "calcRadius",
                            description: "Calculate the radius of the pie chart",
                            update: "(100/222 * min(height,width))"
                        },
                        {
                            name: "cursor",
                            value: "auto"
                        },
                        HoverSignal("arc")
                    ],
                    marks: [
                        {
                            type: "arc",
                            from: { data: "table" },
                            encode: {
                                enter: {
                                    x: { field: { group: "width" }, mult: 0.5 },
                                    y: { field: { group: "height" }, mult: 0.5 },
                                    startAngle: { field: "startAngle" },
                                    endAngle: { field: "endAngle" },
                                    innerRadius: { value: 0 },
                                    outerRadius: { signal: "calcRadius" }
                                },
                                update: {
                                    fill: { scale: "color", field: xAxisName },
                                    fillOpacity: [
                                        {
                                            test: "hoverEvent == datum",
                                            value: 0.9
                                        },
                                        { value: 1 }
                                    ]
                                }
                            }
                        }
                    ],
                    title: {
                        text: this.graphBag.Graph.Title,
                        fontSize: 15
                    }
                };

                if (this.canUseLegend(xAxis)) {
                    spec["legends"] = {
                        type: "symbol",
                        fill: "color",
                        direction: "vertical",
                        orient: "right",
                        offset: 0,
                        strokeColor: "#000",
                        title: xAxisLabel,
                        padding: 7
                    };
                }

                // Add data to Pie Graph Specification
                data["transform"].push({
                    type: "pie",
                    field: "sum_yaxis",
                    startAngle: 0,
                    endAngle: 6.29
                });

                spec["data"] = data;

                return spec;
            },
            getLatestData: function (forceRefresh) {
                this.$emit("getLatestData", forceRefresh);
            },
            adjustLegend: function () {
                // Determine the graph type and whether the legend is vertical or horizontal
                const graphElement = this.$refs.graph;

                // Check if there is no legend don't adjust the graph
                if (!this.canGraphUseLegend) {
                    return;
                }
                if (this.graphBag.Graph.TypeId == this.graphTypeConstant.pie) {
                    const legend = d3.select(graphElement).select(".role-legend g");
                    const legendBoundBox = legend.node().getBBox();
                    const graphBoundingBox = d3.select(graphElement).select("svg").node().getBBox();

                    legend.attr("transform", function (d /*, i*/) {
                        return (
                            "translate(" +
                            d.bounds.x1 +
                            "," +
                            (graphBoundingBox.height / 2 - legendBoundBox.height) +
                            ")"
                        );
                    });
                }

                if (this.graphBag.Graph.TypeId == this.graphTypeConstant.stacked) {
                    const graphElementSelection = d3.select(graphElement);
                    const legend = graphElementSelection.select(".role-legend g");
                    const axisItems = graphElementSelection.select(".role-axis");
                    const markBoundingBox = axisItems.select(".role-axis-grid").node().getBBox();

                    const legendBoundBox = legend.node().getBBox();
                    const graphBoundingBox = graphElementSelection.select("svg").node().getBBox();
                    const axisWidth = graphBoundingBox.width - markBoundingBox.width;
                    legend.attr("transform", function (d /*, i*/) {
                        const result = (graphBoundingBox.width - axisWidth) / 2 - legendBoundBox.width / 2;

                        return "translate(" + result + "," + d.bounds.y1 + ")";
                    });
                }
            },
            async renderGraph(data) {
                const graphComponent = this;
                const vgSpec = graphComponent.getGraphConstructor(data);

                vgSpec["width"] = graphComponent.dimensions.width;
                vgSpec["height"] = graphComponent.dimensions.height;

                const defaultFont = "var(--bs-body-font-family)";
                vgSpec["config"] = {
                    axis: { labelFont: defaultFont, titleFont: defaultFont },
                    title: { font: defaultFont },
                    legend: { labelFont: defaultFont, titleFont: defaultFont }
                };
                vgSpec["padding"] = { top: 2 };

                // Remove previous graph if any exist to let the component know that vega is rendering
                if (graphComponent.view) {
                    graphComponent.view.finalize();
                    graphComponent.view = null;
                }

                const toolTip = graphComponent.toolTip;
                const graphView = await new vega.View(vega.parse(vgSpec))
                    .logLevel(vega.Info)
                    .renderer("svg")
                    .tooltip(function (handler, event, item, value) {
                        // Hide tooltip if nothing to display
                        if (value == null || value === "") {
                            toolTip.display = false;
                            return;
                        }
                        toolTip.display = true;
                        toolTip.content = value;

                        // Notify that the focus is on this graph
                        // Currently this is used to prevent the tool tip from being obscured
                        graphComponent.$emit("focus");

                        // Wait until next tick in order to get the tooltip width and height
                        graphComponent.$nextTick(function () {
                            // Calculate position of tool tip
                            const position = calculatePosition(
                                { x: event.clientX, y: event.clientY },
                                graphComponent.$refs.tooltip,
                                { width: window.innerWidth, height: window.innerHeight },
                                toolTip.offSet.x,
                                toolTip.offSet.y
                            );

                            const graphBoundingBox = graphComponent.$refs.graph.getBoundingClientRect();
                            toolTip.x = position.x - graphBoundingBox.left;
                            toolTip.y = position.y - graphBoundingBox.top;
                        });
                    })
                    .initialize(graphComponent.$refs.graph)
                    .runAsync();

                graphComponent.view = graphView;
                graphComponent.adjustLegend();
                // Remove cursor style being applied by vega view
                graphComponent.$refs.graph.style.removeProperty("cursor");
            }
        }
    };
</script>

<style lang="scss" scoped>
    .graph-menu {
        width: 1rem;
        height: 1rem;
        cursor: pointer;
    }

    .context-menu {
        position: absolute;
        right: 0rem;
    }

    .pc-tooltip-element {
        background-color: var(--bs-white);
        opacity: 0.95;
        border: 0.25rem solid var(--bs-gray-200);
    }

    .auto-cursor {
        cursor: auto;
    }
</style>
